import {DataStatus} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds, workshopRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  ProfitabilityHistory,
  queryParams,
  Section,
  WorkItems,
  workshopSearchParams,
} from '@dms/shared';

import {Nullish, composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface HistoryOfRepairsProps {
  vehicleId: string | Nullish;
}

const CASE_STATE_COLUMNS = ['state'] as const;
const SERVICE_CASE_ID_COLUMNS = ['serviceCaseId'] as const;
const SERVICE_REQUEST_COLUMNS = ['serviceOrderId', 'serviceCaseId'] as const;

export function HistoryOfRepairs(props: HistoryOfRepairsProps) {
  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {vehicleId: props.vehicleId ?? null}]);

  const actionOrderCallback: ActionCallback<typeof SERVICE_CASE_ID_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const serviceCaseId = getColumnData<string>('serviceCaseId')?.value;

    match(actionKey)
      .with('redirectDetail', 'redirectDetail_newTab', () => {
        const orderId = isArray(rowId) ? rowId[0] : rowId;

        window.open(
          composePath(workshopRoutes.serviceCaseDetail, {
            params: {
              id: serviceCaseId ?? '',
            },
            queryParams: {
              [queryParams.COMPONENT_SECTIONS_SECTION]: workshopSearchParams.serviceDetail.orders,
              [queryParams.SERVICE_CASE_ORDER_ID]: orderId,
            },
          })
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const actionServiceRequestsCallback: ActionCallback<typeof SERVICE_REQUEST_COLUMNS> = ({
    actionKey,
    getColumnData,
    rowId,
  }) => {
    const serviceOrderId = getColumnData<string>('serviceOrderId')?.value;
    const serviceCaseId = getColumnData<string>('serviceCaseId')?.value;

    if (!isArray(rowId)) {
      const path = composePath(workshopRoutes.serviceCaseDetail, {
        params: {
          id: serviceCaseId,
        },
        queryParams: {
          [queryParams.COMPONENT_SECTIONS_SECTION]: workshopSearchParams.serviceDetail.orders,
          [queryParams.SERVICE_CASE_ORDER_ID]: serviceOrderId ?? '',
          [queryParams.SERVICE_CASE_OPEN_JOB_ID]: rowId,
        },
      });

      match(actionKey)
        .with('open', () => window.open(path))
        .otherwise(() => catchUnhandledDataGridActions(actionKey));
    }
  };

  const actionCaseCallback: ActionCallback<typeof CASE_STATE_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const serviceCaseState = getColumnData<string>('state')?.value;

    match(actionKey)
      .with('redirectDetail', 'redirectDetail_newTab', () => {
        const serviceCaseId = isArray(rowId) ? rowId[0] : rowId;

        let path;

        if (serviceCaseState === 'DRAFT') {
          path = composePath(workshopRoutes.draftServiceCase, {
            params: {id: serviceCaseId},
            queryParams: {
              [queryParams.COMPONENT_SECTIONS_TAB]: workshopSearchParams.create.serviceJobs,
            },
          });
        } else {
          path = composePath(workshopRoutes.serviceCaseDetail, {
            params: {
              id: serviceCaseId,
            },
            queryParams: {
              [queryParams.COMPONENT_SECTIONS_SECTION]: workshopSearchParams.serviceDetail.orders,
            },
          });
        }

        window.open(path);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <Section
      data-testid={testIds.vehicles.historyOfRepairs('historyOfRepairs')}
      tabs={[
        {
          content: (
            <DataStatus
              isLoading={isNil(props.vehicleId)}
              key="vehicle-history-service-case"
              minHeight={175}
            >
              <Box height={175}>
                <DmsDataGrid
                  gridCode="vehicle-history-service-case"
                  actionCallback={actionCaseCallback}
                  predefinedColumns={CASE_STATE_COLUMNS}
                  queryModifier={queryModifier}
                  data-testid={testIds.vehicles.historyOfRepairs('historyOfRepairs-serviceCases')}
                />
              </Box>
            </DataStatus>
          ),
          queryId: 'serviceCases',
          title: i18n.t('page.workshop.labels.serviceCases'),
        },
        {
          content: (
            <DataStatus
              isLoading={isNil(props.vehicleId)}
              key="vehicle-history-service-order"
              minHeight={175}
            >
              <Box height={175}>
                <DmsDataGrid
                  gridCode="vehicle-history-service-order"
                  actionCallback={actionOrderCallback}
                  predefinedColumns={SERVICE_CASE_ID_COLUMNS}
                  queryModifier={queryModifier}
                  data-testid={testIds.vehicles.historyOfRepairs('historyOfRepairs-serviceOrders')}
                />
              </Box>
            </DataStatus>
          ),
          queryId: 'serviceOrders',
          title: i18n.t('page.workshop.labels.serviceOrders'),
        },
        {
          content: (
            <DataStatus
              isLoading={isNil(props.vehicleId)}
              key="historyOfRepairs-service-requests"
              minHeight={175}
            >
              <Box height={175}>
                <DmsDataGrid
                  gridCode="vehicle-history-order-requests"
                  actionCallback={actionServiceRequestsCallback}
                  predefinedColumns={SERVICE_REQUEST_COLUMNS}
                  queryModifier={queryModifier}
                  data-testid={testIds.vehicles.historyOfRepairs(
                    'historyOfRepairs-serviceRequests'
                  )}
                />
              </Box>
            </DataStatus>
          ),
          queryId: 'serviceRequests',
          title: i18n.t('page.workshop.labels.serviceRequests'),
        },
        {
          content: (
            <WorkItems
              datagridCode="vehicle-history-order-items"
              vehicleId={props.vehicleId}
              data-testid={testIds.vehicles.historyOfRepairs('historyOfRepairs-workItems')}
            />
          ),
          queryId: 'workItems',
          title: i18n.t('entity.orderRequest.labels.workItems'),
        },
        {
          content: (
            <ProfitabilityHistory
              vehicleId={props.vehicleId}
              data-testid={testIds.vehicles.historyOfRepairs('historyOfRepairs-profitability')}
            />
          ),
          queryId: 'profitability',
          title: i18n.t('entity.order.labels.profitability'),
        },
      ]}
    />
  );
}
